import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LucideIcon, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface QuickStatsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
  href?: string;
  className?: string;
}

const QuickStatsCard: React.FC<QuickStatsCardProps> = ({
  title,
  value,
  description,
  icon: Icon,
  trend,
  isLoading,
  href,
  className,
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className={cn("rounded-lg border bg-card p-5", className)}>
        <Skeleton className="h-3 w-20 mb-3" />
        <Skeleton className="h-8 w-16 mb-2" />
        <Skeleton className="h-3 w-28" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-5 group",
        href && "cursor-pointer hover:bg-accent/50 transition-colors",
        className,
      )}
      onClick={href ? () => navigate(href) : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {trend && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium",
              trend.isPositive ? "text-success" : "text-destructive",
            )}
          >
            {trend.isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {trend.isPositive ? "+" : ""}
            {trend.value}%
          </span>
        )}
        {description && (
          <span className="text-xs text-muted-foreground">{description}</span>
        )}
      </div>
    </div>
  );
};

export default QuickStatsCard;
